// react-magnetic-di - temporary comment, for auto dependency injection in tests
import { useCallback, useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';
import { useRelayEnvironment } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	fireErrorAnalytics,
	fireOperationalEvent,
	fireTrackEvent,
} from '@atlassian/ui-modifications-analytics';

import { ON_INIT_CHANGE_ID } from '../../../../../../../common/constants/constants';
import type { FieldMapFromIframe } from '../../../../../../../common/types/field';
import { checkSameFieldValueChanged } from '../../../../../../../common/utils/check-same-field-value-changed';
import { useExecutionContext } from '../../../../../../../controllers/execution-context';
import { useLookupFieldData } from '../../../../../../../controllers/hooks/use-lookup-field-data';
import { useFormMetadata } from '../../../../../../../controllers/issue-adjustments/hooks/use-form-metadata';
import { useLastProcessedChange } from '../../../../../../../controllers/issue-adjustments/hooks/use-last-change';
import { useViewType } from '../../../../../../../controllers/issue-adjustments/hooks/use-view-type';
import { useIssueAdjustmentsActions } from '../../../../../../../controllers/issue-adjustments/main';
import { UiModificationsValidationError } from '../../../../../../../controllers/issue-adjustments/utils/errors/ui-modifications-validation-error';
import { useTenantContext } from '../../../../../../../controllers/tenant-context';
import { getAnalytics } from '../../../../../../../controllers/view-configuration';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { moduleIncludesScreenTabsWriteScopes } from '../utils/filter-based-on-scopes/screen-tabs-data';

import type { SubmitEventPayload } from './types';
import { useFieldsChangesValidation } from './use-fields-changes-validation';
import { useFilterFieldChangesBasedOnScope } from './use-filter-fields-changes-based-on-scope';

type UseOnSubmitProps = {
	moduleScopes: readonly string[];
	appId: string;
};
export const useSubmit = (
	{ moduleScopes, appId }: UseOnSubmitProps
): ((payload: SubmitEventPayload) => Promise<void>) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		updateFields,
		setError,
		unregisterFields,
		setAppErrors,
		updateScreenTabs,
		detectAppliedChangesConflicts,
	} = useIssueAdjustmentsActions();
	const viewType = useViewType();
	const getAsyncLookupData = useLookupFieldData();
	const getFilteredFieldChangesBasedOnScope = useFilterFieldChangesBasedOnScope(moduleScopes);
	const tenantContext = useTenantContext();

	const executionContext = useExecutionContext();
	const environment = useRelayEnvironment();
	const getValidatedFieldsChanges = useFieldsChangesValidation();
	const { lastProcessedChange } = useLastProcessedChange();
	const lastChangedProcessRef = useRef(lastProcessedChange);
	const formMetadata = useFormMetadata();
	const formMetadataRef = useRef(formMetadata);

	formMetadataRef.current = formMetadata;

	useEffect(() => {
		lastChangedProcessRef.current = lastProcessedChange;
	}, [lastProcessedChange]);

	const intl = useIntl();

	const getFieldAttributesForAnalytics = useCallback((fieldsChanges: FieldMapFromIframe) => {
		try {
			const fieldAttributes: { fieldType: string; attributes: string[] }[] = [];
			for (const fieldId in fieldsChanges) {
				if (formMetadataRef.current?.[fieldId]) {
					const { fieldType } = formMetadataRef.current[fieldId];
					const attributes = Object.keys(fieldsChanges[fieldId]);
					fieldAttributes.push({ fieldType, attributes });
				}
			}
			return JSON.stringify(fieldAttributes);
			// oxlint-disable-next-line no-unused-vars
		} catch (_error) {
			return 'NULL';
		}
	}, []);

	return useCallback(
		async ({
			fieldsChanges: fieldsChangesUnnormalized,
			screenTabsChanges,
			changeId,
		}: SubmitEventPayload) => {
			let isSameFieldValueChanged: boolean = false;
			let fieldType: string | null = null;
			try {
				const fieldsChanges = getFilteredFieldChangesBasedOnScope(fieldsChangesUnnormalized);
				const validatedFieldsChanges = await getValidatedFieldsChanges(fieldsChanges);
				const lookupDataFromApi = await getAsyncLookupData(validatedFieldsChanges);

				await updateFields({
					changeId,
					fieldsChanges, // TODO: We should pass validatedFieldsChanges instead of fieldsChanges, UIM-2214
					lookupDataFromApi,
					tenantContext,
					executionContext,
					environment,
					appId,
					intl,
					createAnalyticsEvent,
				});

				if (
					screenTabsChanges &&
					moduleIncludesScreenTabsWriteScopes(
						viewType,
						createAnalyticsEvent,
						moduleScopes,
						screenTabsChanges,
					)
				) {
					updateScreenTabs({ screenTabsChanges, appId, changeId });
				}

				detectAppliedChangesConflicts({
					appId,
					changeId,
				});

				getAnalytics(viewType).stopTTI({
					completionStatus: 'success',
					viewType,
				});

				isSameFieldValueChanged = checkSameFieldValueChanged(
					fieldsChanges,
					lastChangedProcessRef.current,
					changeId,
				);

				if (
					lastChangedProcessRef.current?.fieldId &&
					formMetadataRef.current?.[lastChangedProcessRef.current.fieldId]
				) {
					const fieldId = lastChangedProcessRef.current.fieldId;
					const { fieldType: fieldTypeFromFormMetadata } = formMetadataRef.current[fieldId];
					fieldType = fieldTypeFromFormMetadata;
				}

				fireTrackEvent(createAnalyticsEvent, {
					action: 'success',
					actionSubject: `${viewType}jiraUiModificationsUseSubmit`,
					attributes: {
						isSameFieldValueChanged,
						isOnInit: changeId === ON_INIT_CHANGE_ID,
						fieldType,
						submittedFields: getFieldAttributesForAnalytics(fieldsChanges),
					},
				});

				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				setError({
					errorCode: 'USE_SUBMIT_ERROR_CODE',
				});

				getAnalytics(viewType).stopTTI({
					completionStatus: 'error',
					viewType,
				});

				if (err instanceof UiModificationsValidationError) {
					fireOperationalEvent(createAnalyticsEvent, {
						action: 'failed',
						actionSubject: 'jiraUiModifications.changesValidation',
						attributes: {
							type: err.validationErrorType,
							...err.attributes,
							viewType,
						},
					});

					// Setting app errors to pass them to the onErrorCallback
					setAppErrors({ errors: err.attributes.errors, appId });
					return;
				}

				fireErrorAnalytics({
					error: err,
					id: 'useSubmit',
					viewType,
					attributes: {
						errorCause: err?.cause,
						isSameFieldValueChanged,
						isOnInit: changeId === ON_INIT_CHANGE_ID,
						fieldType,
					},
					event: createAnalyticsEvent({}),
				});
			} finally {
				unregisterFields({ changeId, appId });
			}
		},
		[
			getFilteredFieldChangesBasedOnScope,
			getValidatedFieldsChanges,
			getAsyncLookupData,
			updateFields,
			tenantContext,
			executionContext,
			environment,
			appId,
			intl,
			viewType,
			createAnalyticsEvent,
			moduleScopes,
			updateScreenTabs,
			detectAppliedChangesConflicts,
			setError,
			setAppErrors,
			unregisterFields,
			getFieldAttributesForAnalytics,
		],
	);
};
